import { useState, useEffect } from 'react';
import {
  UIBox,
  UITypography,
  UICard,
  UICardContent,
  UIInput,
  UIButton,
  UIAlertDestructive,
  UIAlertSuccess,
  UISkeleton,
  UISeparator,
  UIAlertDialogDestructive,
  UIDialog,
  UIDialogContent,
  UIDialogHeader,
  UIDialogTitle,
  UIDialogFooter,
} from '@workspace/ui/composed';
import { Loader2, KeyRound } from 'lucide-react';
import { api } from '../utils/api-client';
import { getUserEmail, doLogout } from '../utils/keycloak';
import { useTranslations } from '../i18n/useTranslations';
import { t as translate } from '../i18n';

interface UserProfile {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  attributes?: Record<string, string[]>;
}

export default function PersonalInfoPage() {
  const { t } = useTranslations('pages.personalInfo');
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({});
  const [error, setError] = useState<string | null>(null);

  // Password change states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPersonalInfo();
      setProfile(data);
    } catch (err) {
      setError(t('loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      setError(null);
      const email = getUserEmail();
      if (!email) {
        throw new Error('Email not found');
      }
      await api.withdraw(email);
      doLogout();
    } catch (err) {
      setError(t('withdrawError'));
      console.error('Failed to withdraw:', err);
      setWithdrawing(false);
    }
  };

  const handleOpenPasswordDialog = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setPasswordError(null);
    setPasswordSuccess(false);
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordError(null);
  };

  const handlePasswordFormChange = (field: keyof typeof passwordForm) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPasswordForm({ ...passwordForm, [field]: event.target.value });
    setPasswordError(null);
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
      setPasswordError(t('passwordRequired'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setPasswordError(t('passwordMismatch'));
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordError(null);
      await api.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordSuccess(true);
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setPasswordSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { status?: number } }).response;
        if (response?.status === 400) {
          setPasswordError(t('currentPasswordIncorrect'));
        } else {
          setPasswordError(t('changePasswordError'));
        }
      } else {
        setPasswordError(t('changePasswordError'));
      }
      console.error('Failed to change password:', err);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <UIBox className="space-y-6">
        {/* Page Header Skeleton */}
        <UIBox>
          <UISkeleton className="h-7 w-40 mb-2" />
          <UISkeleton className="h-4 w-72" />
        </UIBox>

        {/* Form Skeleton */}
        <UICard>
          <UICardContent className="p-6 space-y-6">
            <UISkeleton className="h-5 w-32" />
            <UIBox className="space-y-4">
              <UISkeleton className="h-16 w-full" />
              <UISkeleton className="h-16 w-full" />
            </UIBox>
            <UISeparator />
            <UISkeleton className="h-5 w-32" />
            <UIBox className="grid grid-cols-2 gap-4">
              <UISkeleton className="h-16" />
              <UISkeleton className="h-16" />
            </UIBox>
          </UICardContent>
        </UICard>
      </UIBox>
    );
  }

  return (
    <UIBox className="space-y-6">
      {/* Page Header */}
      <UIBox>
        <UITypography variant="h4" className="font-semibold">
          {t('title')}
        </UITypography>
        <UITypography variant="muted" className="mt-1">
          {t('description')}
        </UITypography>
      </UIBox>

      {/* Alerts */}
      {error && (
        <UIAlertDestructive>
          {error}
        </UIAlertDestructive>
      )}

      {/* Form Card */}
      <UICard>
        <UICardContent className="p-6 space-y-6">
          {/* Account Section */}
          <UIBox className="space-y-4">
            <UIBox className="flex items-center justify-between">
              <UITypography variant="p" className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Account
              </UITypography>
              <UIButton
                variant="default"
                size="sm"
                onClick={handleOpenPasswordDialog}
                className="gap-2"
              >
                <KeyRound className="h-4 w-4" />
                {t('changePassword')}
              </UIButton>
            </UIBox>

            {/* Email - Read Only */}
            <UIBox className="space-y-2">
              <UITypography variant="p" className="text-sm font-medium">
                {t('email')}
              </UITypography>
              <UIBox className="flex items-center gap-3 h-9 px-3 bg-muted/50 rounded-md border border-border">
                <UITypography variant="p" className="text-muted-foreground truncate">
                  {profile.email}
                </UITypography>
              </UIBox>
              <UITypography variant="muted" className="text-xs">
                {t('emailHelperText')}
              </UITypography>
            </UIBox>
          </UIBox>

          <UISeparator />

          {/* Profile Section */}
          <UIBox className="space-y-4">
            <UITypography variant="p" className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Profile
            </UITypography>

            <UIBox className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UIBox className="space-y-2">
                <UITypography variant="p" className="text-sm font-medium">
                  {t('firstName')}
                </UITypography>
                <UIBox className="flex items-center gap-3 h-9 px-3 bg-muted/50 rounded-md border border-border">
                  <UITypography variant="p" className="text-muted-foreground truncate">
                    {profile.firstName}
                  </UITypography>
                </UIBox>
                <UITypography variant="muted" className="text-xs">
                  {t('firstNameHelperText')}
                </UITypography>
              </UIBox>
              <UIBox className="space-y-2">
                <UITypography variant="p" className="text-sm font-medium">
                  {t('lastName')}
                </UITypography>
                <UIBox className="flex items-center gap-3 h-9 px-3 bg-muted/50 rounded-md border border-border">
                  <UITypography variant="p" className="text-muted-foreground truncate">
                    {profile.lastName}
                  </UITypography>
                </UIBox>
                <UITypography variant="muted" className="text-xs">
                  {t('lastNameHelperText')}
                </UITypography>
              </UIBox>
            </UIBox>
          </UIBox>
        </UICardContent>
      </UICard>

      {/* Delete Account Section */}
      <UIBox className="flex justify-end">
        <UIAlertDialogDestructive
          title={t('deleteAccountConfirmTitle')}
          description={t('deleteAccountConfirmDescription')}
          cancelText={t('cancel')}
          confirmText={t('confirm')}
          onConfirm={handleWithdraw}
        >
          <UIButton className="bg-red-600 hover:bg-red-700 text-white" disabled={withdrawing}>
            {withdrawing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('withdrawing')}
              </>
            ) : (
              t('deleteAccount')
            )}
          </UIButton>
        </UIAlertDialogDestructive>
      </UIBox>

      {/* Change Password Dialog */}
      <UIDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <UIDialogContent size="sm">
          <UIDialogHeader>
            <UIDialogTitle>{t('changePassword')}</UIDialogTitle>
          </UIDialogHeader>

          <UIBox className="space-y-4 py-4">
            {passwordError && (
              <UIAlertDestructive>
                {passwordError}
              </UIAlertDestructive>
            )}

            {passwordSuccess && (
              <UIAlertSuccess>
                {t('changePasswordSuccess')}
              </UIAlertSuccess>
            )}

            <UIInput
              label={t('currentPassword')}
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordFormChange('currentPassword')}
              disabled={changingPassword}
            />

            <UIInput
              label={t('newPassword')}
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordFormChange('newPassword')}
              disabled={changingPassword}
            />

            <UIInput
              label={t('confirmNewPassword')}
              type="password"
              value={passwordForm.confirmNewPassword}
              onChange={handlePasswordFormChange('confirmNewPassword')}
              disabled={changingPassword}
            />
          </UIBox>

          <UIDialogFooter>
            <UIButton
              variant="outline"
              onClick={handleClosePasswordDialog}
              disabled={changingPassword}
            >
              {translate('common.cancel')}
            </UIButton>
            <UIButton
              variant="default"
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="gap-2 min-w-[100px]"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('changingPassword')}
                </>
              ) : (
                t('changePassword')
              )}
            </UIButton>
          </UIDialogFooter>
        </UIDialogContent>
      </UIDialog>
    </UIBox>
  );
}
